/**
 * COMMANDER OVERKILL — entry point and integration.
 *
 * Every element module is built against the `WorldState`/`System` contract in
 * core/types.ts and knows nothing about the others. This file is the only place
 * they meet, and the only place that owns `world.troops` — a gate reports that
 * it was crossed, it does not pay itself.
 *
 * UPDATE ORDER IS LOAD-BEARING and is spelled out explicitly below rather than
 * looped over an array, because getting it wrong produces a one-frame lag that
 * looks like a physics bug and isn't.
 */

import * as THREE from "three";
import { GameLoop } from "./core/loop";
import { StateMachine } from "./core/state";
import { createStage } from "./core/renderer";
import { createZoom } from "./core/zoom";
import { bus } from "./core/events";
import { createWorld, MAX_TROOPS } from "./core/types";
import type { System, WeaponKind, WeaponTier } from "./core/types";
import { WEAPON_ROCKET, WEAPON_SPLASH } from "./core/types";
import { TouchDriver, clamp } from "./input/touch";
import { createCorridor, CORRIDOR_HALF_WIDTH, CORRIDOR_LENGTH } from "./mechanics/lane";
import { createSquad } from "./entities/squad";
import {
  createBullets,
  damagePerPass,
  effectiveShooters,
  MAX_STREAMS,
} from "./mechanics/bullets";
import {
  barrelHp,
  barrelPayout,
  damageOnSegment,
  ELITE_PASS_SHARE,
  enemyHp,
  CAGE_PASS_SHARE,
  OGRE_PASS_SHARE,
  WALKER_PASS_SHARE,
} from "./mechanics/pacing";
import { createDirector, createRng } from "./mechanics/director";
import type { Placement } from "./mechanics/director";
import {
  composeAutoRow,
  createGates,
  MERCY_TROOPS,
  rowHasReward,
  rowPlacement,
} from "./mechanics/gates";
import { createBarrels } from "./entities/barrels";
import { createEnemies } from "./entities/enemies";
import { createBoss } from "./entities/boss";
import { ALLY_STREAMS, createAlly } from "./entities/ally";
import type { BossKind } from "./entities/boss";
import { createPickups } from "./entities/pickups";
import type { PickupKind } from "./entities/pickups";
import { createFloaters } from "./ui/floaters";
import { createGrowthFx } from "./entities/growthfx";
import { createBossBar } from "./ui/bossbar";
import { createTroopCount } from "./ui/troopcount";
import { createLoadout } from "./ui/loadout";
import { createStreak } from "./ui/streak";
import { createCommander } from "./ui/commander";
import { createLevelCard } from "./ui/levelcard";
import type { PerkOffer } from "./ui/levelcard";
import { createNetPop } from "./ui/netpop";
import { mountPerfOverlay } from "./ui/perf";
import type { CanvasTexture, Mesh, MeshLambertMaterial } from "three";

const canvas = document.getElementById("stage") as HTMLCanvasElement;
const ui = document.getElementById("ui") as HTMLElement;

const stage = createStage(canvas);
const state = new StateMachine();
const touch = new TouchDriver(canvas);
const world = createWorld(new THREE.Vector3());
const zoom = createZoom(stage.camera, stage.scene);

const corridor = createCorridor();
stage.scene.add(corridor.object);

// The reference has no hero avatar — the squad IS the player. entities/commander.ts
// stays in the tree because the brief specs a named hero and specs/prd.md still
// has that question open; it is simply not mounted.
const squad = createSquad(stage.scene);
const bullets = createBullets(stage.scene);
// autoSpawn OFF: the director owns the corridor now. Leaving the gate module
// pacing itself is precisely the second metronome that caused the overlap.
const gates = createGates(stage.scene, { autoSpawn: false });
const barrels = createBarrels(stage.scene);
const enemies = createEnemies(stage.scene);
// Punctuation. Everything else on the road is a decision you make in passing;
// this is one that stands there and waits for an answer. See entities/boss.ts.
const boss = createBoss(stage.scene);
// The rescued robot, and the cage it arrives in. One per level — see
// entities/ally.ts.
const ally = createAlly(stage.scene);
// What rides a barrel. Not an enemy — see the note in entities/pickups.ts.
const pickups = createPickups(stage.scene);
const floaters = createFloaters(stage.scene);
const growthFx = createGrowthFx(stage.scene);
const bossBar = createBossBar(ui);
// The only number on screen used to be the boss bar's, which counts DOWN.
// Players read it as their army. Now the army has its own readout.
const troopCount = createTroopCount(ui);
// Crowd size is one axis; what the crowd is CARRYING is the other, and until
// this existed the second one was invisible — see ui/loadout.ts.
const loadout = createLoadout(ui);
// Going around a row is free, which left the safe line with nothing at stake.
// This is what prices it — see ui/streak.ts.
const streak = createStreak(ui);
// The joke the game is named after. Files reports; never breaks.
const commander = createCommander(ui);
// The level pill, the screen between levels, and the upgrade that screen exists
// to offer — see ui/levelcard.ts.
const levelCard = createLevelCard(ui);
// The per-unit +1s say WHICH soldiers; they are bad at saying HOW MANY when a
// row resolves three segments at once. This is the running total on top.
const netPop = createNetPop(ui, stage.camera);

const renderables: System[] = [
  squad,
  bullets,
  gates,
  barrels,
  enemies,
  boss,
  ally,
  pickups,
  floaters,
  growthFx,
  bossBar,
  troopCount,
  loadout,
  streak,
  commander,
  levelCard,
  netPop,
];

// ── Rewards ────────────────────────────────────────────────────────────────

/**
 * One payout path for every reward source, so the growth beat is identical
 * whether it came from a gate or a barrel. Floaters are spawned per unit
 * ACTUALLY gained rather than per the reward's face value — a reward clipped by
 * MAX_TROOPS must not promise troops that never arrived.
 */
function payTroops(amount: number): void {
  // MEDICS APPLY HERE, at the one seam every reward and every casualty passes
  // through, so a rank of it is worth the same against a red gate, a walker that
  // reached the line and a boss that broke through. A defensive upgrade that
  // only covered one of those would be a footnote; this one is legible as
  // "you lose fewer men".
  if (amount < 0 && perks.medic > 0) amount *= 1 - medicSave(perks.medic);
  const before = world.troops;
  world.troops = clamp(world.troops + amount, 0, MAX_TROOPS);
  const delta = world.troops - before;
  // ELITES ARE THE LAST THING YOU LOSE. Ordinary losses eat the crowd around
  // them, so a loss only reaches the specials once the army has been ground down
  // to the specials themselves — which is the read we want: a shattered squad is
  // a handful of veterans and gunners, not a random survivor.
  armCarriers();
  if (delta === 0) return;

  // He does not remark on every scratch: a tenth of the army in one payout is a
  // report, two men is not. The STREAK is not broken here — see the gate handler
  // and the note on why it is a barrier streak rather than a casualty one.
  if (delta < 0 && before > 0 && -delta / before >= LOSS_REMARK_SHARE) commander.say("loss");

  if (world.troops > biggestCrowd) biggestCrowd = world.troops;
  netPop.add(delta);
  if (delta > 0) {
    growthFx.play(squad.center, squad.radius);
    noteGrowth();
  }
  // The floaters are NOT spawned here any more. A `+1` has to sit over the
  // soldier it is counting, and that soldier does not exist yet — the squad
  // creates him on its next update. `drainSquadEvents()` runs after that and
  // places the glyphs on the actual bodies.
}

/** Scratch for the squad's spawn/death reports. Allocated once. */
const bodyEvents = Array.from({ length: 32 }, () => new THREE.Vector3());

/**
 * Put a glyph on every body that just appeared or fell.
 *
 * Runs immediately after `squad.update`, because that is the tick where the new
 * units get their positions and the dying ones start toppling. Any earlier and
 * there is nothing to point at; any later and the glyph is a frame behind the
 * body it belongs to.
 */
function drainSquadEvents(): void {
  const gained = squad.takeSpawns(bodyEvents, bodyEvents.length);
  if (gained > 0) floaters.spawnAt(bodyEvents, gained, "gain");
  const lost = squad.takeDeaths(bodyEvents, bodyEvents.length);
  if (lost > 0) floaters.spawnAt(bodyEvents, lost, "loss");
}

// `troops`, not `value`: the panel's number is what a WHOLE army walking through
// that one segment would collect, and a wide crowd only ever sends part of
// itself through any given segment. See resolve() in mechanics/gates.ts.
/** Dev-only record of what each segment paid. Empty and untouched in production. */
const payoutLog: { troops: number; value: number; share: number; reward: boolean }[] = [];

/**
 * Streak length at which the Commander notices. Four clean segments is about a
 * minute of not putting a foot wrong, which is worth a remark; two is luck.
 */
const STREAK_REMARK = 4;
/** Share of the army lost in one payout that the Commander will remark on. */
const LOSS_REMARK_SHARE = 0.1;

/**
 * A STREAK COUNTS ROWS YOU CAME OUT AHEAD ON, NOT ROWS WITH NO RED IN THEM.
 *
 * "Cross without touching a red" is the version that reads best in a sentence
 * and it measured out at a flat 1× for an entire run — the raw count reached 1
 * on 14 samples out of 400 and 2 on none of them. The reason is structural: a
 * segment is crossed if the crowd covers a fraction of it (`CROSS_FRACTION` in
 * mechanics/gates.ts), and past a few dozen troops the army is wider than a
 * whole row, so it takes every segment in the row whatever it aims at. A no-red
 * streak is unavailable by construction to any army big enough to want one.
 *
 * Net-positive is the rule that survives that, and it is a better rule anyway:
 * it is achievable at every size, it is broken by a genuinely bad row rather
 * than by geometry, and STEERING is the thing that turns a bad row into a good
 * one — put the crowd's weight on the blue and the red only clips its edge. The
 * multiplier is what pays for learning that.
 *
 * A row's segments all resolve inside one `gates.update`, so they are totalled
 * here and judged in `tick()` immediately after it.
 */
let rowNet = 0;
let rowHits = 0;
/** Dev-only tally of how rows are landing, for tuning the ladder. */
let rowsWon = 0;
let rowsLost = 0;

gates.onResolve((hit) => {
  if (import.meta.env.DEV) {
    payoutLog.push({
      troops: hit.troops,
      value: hit.value,
      share: Number(hit.share.toFixed(3)),
      reward: hit.reward,
    });
  }
  rowHits++;
  rowNet += hit.troops;
  // The multiplier a row pays is the one carried INTO it. Growing it on the
  // strength of the row it is being applied to would pay the bonus before it
  // was earned.
  payTroops(hit.troops > 0 ? Math.round(hit.troops * streak.multiplier) : hit.troops);
});

/** Judge the row that just resolved, if one did. Called from `tick()` straight
 *  after `gates.update()`, which is where segments resolve. */
function settleRow(): void {
  if (rowHits === 0) return;
  const net = rowNet;
  rowHits = 0;
  rowNet = 0;
  if (import.meta.env.DEV) {
    if (net > 0) rowsWon++;
    else rowsLost++;
  }
  if (net > 0) {
    const before = streak.count;
    streak.bank();
    if (before + 1 === STREAK_REMARK) commander.say("streak");
  } else if (streak.breakStreak()) {
    commander.say("loss");
  }
}

/**
 * A DESTROYED BARREL RECRUITS ITS RIDER.
 *
 * The figures standing on barrels were built as hostile "gold rim-lit elites",
 * on the strength of a line in REFERENCE.md that turned out to be a misread of
 * the footage. `reference-media/clip1a` settles it: they wear the PLAYER's blue
 * helmet and pale uniform with a gold upgrade glow, while the actual enemies in
 * the same frames are green zombies and brown walkers. Shooting a barrel is
 * supposed to free an ally, not drop an attacker.
 *
 * So the rider is removed rather than released, and the barrel pays a recruit
 * bonus on top of its normal payout. That bonus is what makes shooting cover
 * worth doing for its own sake instead of only clearing a path.
 *
 * AND THE RECRUIT IS GENUINELY STRONGER. He arrives with three ordinary bodies
 * (the bonus) AND as an elite: bigger, gold, and worth four rifles
 * (`ELITE_SHOOTER_WEIGHT` in mechanics/bullets.ts). Before that, "recruit" was
 * three anonymous troops with a different explosion — and the reference's whole
 * point is that the figure on the barrel is SOMEBODY. A reward you cannot pick
 * out of the crowd a second later is not a reward anyone steers for.
 */
const RECRUIT_BONUS = 3;
/** Elites gained per recruit pickup. One, so the gold in the crowd is a count of
 *  barrels you committed to rather than a colour the whole army drifts toward. */
const RECRUIT_ELITES = 1;
/**
 * What a weapon pickup is worth, as a multiplier on the axis it upgrades.
 *
 * Multiplicative and uncapped-looking, but the rate is clamped by the bullet
 * governor and the damage by `FIREPOWER_MAX`, so a lucky run of barrels cannot
 * trivialise the rest of a level. 1.3 is big enough to feel on the very next
 * barrel and small enough that one pickup is not the whole run.
 */
/**
 * A weapon pickup ARMS TROOPS. It does not raise a hidden multiplier.
 *
 * Mischa put the ask exactly: a rocket launcher with a 10 next to it should mean
 * ten of your soldiers are carrying rocket launchers and firing rockets. So a
 * pickup adds carriers, the squad draws the weapon on them, and their streams
 * fire the matching projectile. `firepower` and `fireRate` are still the numbers
 * the weapon model runs on — they are simply DERIVED from the head count now, in
 * `armCarriers()` below, which is the only place either is written.
 */
const MINIGUN_CREW = 4;
const ROCKET_CREW = 3;
/**
 * The counter crews, and they are SMALLER than the two above on purpose.
 *
 * A flamer or a freezer is worth three times an ordinary rifle against the
 * right target and a third of one against the wrong target, so a crate of them
 * is a bet rather than a stat. Handing out four at a time would make the bet
 * cheap; three and two make you notice which one you took.
 */
const FLAMER_CREW = 3;
const FREEZER_CREW = 2;
/**
 * What one carrier is worth on its axis. Small, because the count grows.
 *
 * ALL FOUR CREWS CONTRIBUTE, AND THAT IS A CORRECTION. The first version of the
 * counter weapons gave a flamer and a freezer no global contribution at all —
 * their whole value was the counter multiplier on their own rounds. Measured,
 * that made them strictly worse than a rocket crate at every army size, because
 * a rocketeer raises `firepower` for EVERY soldier: fifteen rocketeers in a
 * two-hundred-strong army is worth more than a hundred riflemen, and fifteen
 * flamers hitting three times as hard is worth forty-five. The counter table was
 * being swamped by an economy it could not see, and a weapon nobody should ever
 * pick up is not a decision.
 *
 * So the two new crews are PEERS on the existing two axes, at a slightly lower
 * rate because they also carry a counter. Which crate you want is then decided
 * by what is coming down the road, which is the entire point of the exercise.
 */
const GUNNER_RATE = 0.045;
const ROCKETEER_POWER = 0.06;
const FLAMER_POWER = 0.045;
const FREEZER_RATE = 0.03;
/** Ceilings, so a long run cannot stack its way out of the difficulty curve. */
const FIRE_RATE_MAX = 4;
const FIREPOWER_MAX = 6;

/**
 * Re-derive the weapon multipliers from the carrier counts, and keep the counts
 * inside the crowd.
 *
 * Called after anything that changes `troops`, `gunners` or `rocketeers`. The
 * three specials are disjoint — one job per soldier — so they are trimmed in
 * priority order rather than clamped independently: elites first (they are the
 * investment that survives), then gunners, then rocketeers.
 */
function armCarriers(): void {
  world.elites = Math.min(world.elites, world.troops);
  world.gunners = Math.min(world.gunners, Math.max(0, world.troops - world.elites));
  world.rocketeers = Math.min(
    world.rocketeers,
    Math.max(0, world.troops - world.elites - world.gunners),
  );
  // The counter crews are last in the priority order for the same reason
  // rocketeers are before them: a loss eats the least-invested job first, so a
  // shattered army is still the veterans and the heavy weapons.
  const armed = world.elites + world.gunners + world.rocketeers;
  world.flamers = Math.min(world.flamers, Math.max(0, world.troops - armed));
  world.freezers = Math.min(
    world.freezers,
    Math.max(0, world.troops - armed - world.flamers),
  );
  world.fireRate = Math.min(
    FIRE_RATE_MAX,
    1 + world.gunners * GUNNER_RATE + world.freezers * FREEZER_RATE,
  );
  world.firepower = Math.min(
    FIREPOWER_MAX,
    1 + world.rocketeers * ROCKETEER_POWER + world.flamers * FLAMER_POWER,
  );
}

barrels.onDestroyed((_id, tag, _x, _z, maxHp) => {
  payTroops(Math.round(barrelPayout(maxHp) * (1 + perks.salvage * PERK_SALVAGE_SHARE)));
  if (tag < 0) return;

  const kind = pickups.kindOf(tag);
  pickups.collect(tag);
  if (kind === "recruit") {
    payTroops(RECRUIT_BONUS);
    // After the payout, so the elite has bodies to be one of. payTroops clamps
    // elites down to the troop count, and doing this first would have the clamp
    // eat the recruit at low counts.
    world.elites = Math.min(world.troops, world.elites + RECRUIT_ELITES);
  } else if (kind === "minigun") {
    world.gunners += MINIGUN_CREW;
  } else if (kind === "rocket") {
    world.rocketeers += ROCKET_CREW;
  } else if (kind === "flamer") {
    world.flamers += FLAMER_CREW;
    commander.say("flamer");
  } else if (kind === "freezer") {
    world.freezers += FREEZER_CREW;
    commander.say("freezer");
  }
  armCarriers();
});

/**
 * AN ENEMY THAT REACHES YOU COSTS TROOPS.
 *
 * `enemies.ts` has fired `onBreached` since it was written and nothing was
 * listening, so walking into the crowd did literally nothing — enemies were
 * decoration you shot at for a boss bar that is itself a placeholder. Combat had
 * no stake in either direction.
 *
 * The cost is deliberately small. It routes through `payTroops`, so a breach
 * rains the same red `-1`s a red gate does and reads as the same kind of event,
 * and it is the thing that makes enemy hit points worth scaling at all: a wave
 * that survives long enough to arrive now has a consequence when it does.
 */
/**
 * What one surviving enemy body costs when it reaches the crowd, as a share of
 * the army — and this is the price of the "or fight through them" half of every
 * blockade.
 *
 * It used to be a flat count per UNIT: one troop for a whole eight-strong pack,
 * whatever the army size. That made shooting a pack pointless and walking
 * through one free, which measured out exactly as you would expect once the
 * player could dodge a gate row — the autopilot's median run went from 436 to
 * 660 troops with zero wipes, because "go around" had no downside anywhere on
 * the board. A pack that arrives intact now costs about 12% of the army, which
 * is the same order as a red segment; a pack you shot down to two costs 3%.
 *
 * Proportional for the same reason gate penalties are (see PENALTY_BANDS in
 * mechanics/gates.ts): a fixed price is brutal early and irrelevant late.
 */
const BREACH_SHARE = 0.015;
/** Multiplier per kind, on top of the per-body share. Elites and bikers are one
 *  body but hit like several. */
const BREACH_COST: Record<string, number> = { pack: 1, elite: 3, biker: 3, ogre: 10 };
enemies.onBreached((_id, kind, _hp, bodies) => {
  // FREE while the squad is tiny, on the same threshold the gate mercy rule
  // uses. A pack is eight bodies and a two-troop army cannot kill any of them,
  // so charging for every breach turns the opening into a death spiral: too
  // weak to kill, so you lose troops, so you are weaker. Combat starts costing
  // once the army is established enough to do something about it.
  if (world.troops < MERCY_TROOPS) return;
  const weight = (BREACH_COST[kind] ?? 1) * Math.max(1, bodies);
  // THE ROBOT STEPS IN FRONT. Only for a breach worth stepping in front of —
  // spending its cooldown on a single stray walker would mean it was never there
  // for the pack behind it — and only once every few seconds, so it blunts a
  // horde rather than deleting one.
  if (weight >= HITSTOP_BREACH_WEIGHT && ally.guard()) {
    zoom.shake(SHAKE_BOSS_HIT * 0.4);
    return;
  }
  if (weight >= HITSTOP_BREACH_WEIGHT) {
    hitStop(HITSTOP_BREACH);
    // Straight down. A breach arrives from up the road rather than from a side,
    // so there is no sideways direction to honour — the crowd just takes it.
    zoom.punch(0, -1, PUNCH_BREACH);
  }
  payTroops(-Math.max(1, Math.round(world.troops * BREACH_SHARE * weight)));
});

/* ── Bosses ───────────────────────────────────────────────────────────────
 *
 * The bar used to be driven by a kill counter because there was no boss to put
 * in it — a red capsule counting down from 80 to nothing in particular. It
 * shows an actual creature's hit points now, which is what the reference does
 * (`clip1a/f_030` → `f_033`: 350 down to 46 in a second of sustained fire).
 */

/** The order bosses arrive in, cycled. Authored rather than rolled: the first
 *  boss a player ever meets should be the one whose attack is easiest to read,
 *  and the hive — which punishes slow killing with bodies you fight later —
 *  should not be it. */
const BOSS_ORDER: readonly BossKind[] = ["brute", "roller", "hive"];
let bossIndex = 0;
/** Whether the bar is currently up, so it is hidden exactly once when a fight
 *  ends rather than poked every tick for the rest of the run. */
let bossShowing = false;

/**
 * How many full passes of fire a boss is worth, per kind.
 *
 * A "pass" is EFFECTIVE_PASS_SECONDS (4.25 s) of the whole army firing into one
 * target, so 1.5 means a brute takes six and a half seconds of undivided
 * attention. Its patience is 9 s. That gap is the entire encounter design: an
 * army that commits everything wins with room to spare, and an army that spends
 * a third of the fight shooting barrels on the far kerb does not — which is
 * exactly the decision the whole thing exists to pose.
 *
 * The roller is the firepower check, so it asks for the most; the hive asks for
 * the least because failing to kill it fast has its own separate price.
 */
const BOSS_PASS_SHARE: Record<BossKind, number> = { brute: 1.65, roller: 2.1, hive: 1.3 };
/**
 * How each successive boss is scaled: `BASE + STEP × encounter`.
 *
 * The first is deliberately UNDER its nominal weight. Measured, at the troop
 * counts a run actually reaches by the 168 m mark, a full-weight brute takes a
 * bare thirty-strong army twelve seconds to kill against nine seconds of
 * patience — an unwinnable first meeting, which teaches the wrong lesson about
 * a mechanic the player has never seen before. At 0.7 the same army kills it
 * with a second to spare if it commits everything, which is the lesson.
 *
 * By the third encounter the multiplier is 1.4, and a bare army cannot make it
 * in time at all. That is the intended shape: the first boss teaches, the
 * second tests, and the third one asks what you spent the run buying.
 */
const BOSS_RAMP_BASE = 0.7;
const BOSS_RAMP_STEP = 0.35;

/**
 * How much of the army's weapon upgrades a boss's hit points take account of.
 *
 * EVERY OTHER HP MODEL IN THIS GAME IGNORES UPGRADES ENTIRELY, on purpose: a
 * barrel that got tougher every time you picked up a launcher would make the
 * launcher worthless (see the note on `firepower` in core/types.ts). A boss is
 * the one exception, and it earns it by being the one piece of content whose
 * whole job is to ask whether the army is strong enough. Measured, a kitted
 * ninety-strong army kills an unadjusted brute in 2.5 s against a bare army's
 * 7.5 s — a boss that evaporates in two seconds is not a check, it is a cutscene
 * with a health bar.
 *
 * At 0.6 an upgrade is still worth roughly two fifths of its raw value against a
 * boss and its full value against everything else on the road, which keeps the
 * pickup a real prize without letting it delete the encounter.
 */
const BOSS_UPGRADE_BITE = 0.6;
/** Floor, for a boss met by a very small army. Low enough to be killable at the
 *  troop counts the first one actually arrives at. */
const BOSS_HP_FLOOR = 45;

function bossHp(kind: BossKind, encounter: number): number {
  const share = BOSS_PASS_SHARE[kind] * (BOSS_RAMP_BASE + encounter * BOSS_RAMP_STEP);
  // DAMAGE PER PASS, NOT enemyHp — and the difference is the whole calibration.
  // `enemyHp` scales its answer by `laneCoverage`, the share of a curtain of
  // fire that a 1.7 m barrel face intercepts, which for a wide crowd is under a
  // third. A boss is six metres across and catches the whole curtain, so pricing
  // one through that path made it four times cheaper than intended: the first
  // build's brute died in a second and a half.
  // Elites shoot like four soldiers each, so they go in as the head count the
  // guns actually amount to rather than the number of bodies.
  const guns = effectiveShooters(world.troops, world.elites);
  const upgrade = 1 + (world.firepower * world.fireRate - 1) * BOSS_UPGRADE_BITE;
  const raw = Math.max(
    BOSS_HP_FLOOR,
    Math.round(damagePerPass(tierFor(world.troops), guns, bullets.tuning) * share * upgrade),
  );
  // Two significant figures, because unlike a barrel's this number is READ: it
  // is the biggest thing on the HUD and it counts down in front of the player
  // for ten seconds. "5,400" is a boss's health; "5,449" is a spreadsheet cell.
  // Deliberately NOT niceHp() from pacing.ts — that ladder tops out at 250,
  // which is a barrel's world, and it would quietly cap every boss in the game
  // at a quarter of what the first one is supposed to cost.
  const mag = Math.pow(10, Math.max(0, Math.floor(Math.log10(raw)) - 1));
  return Math.round(raw / mag) * mag;
}

/**
 * What being caught by a boss attack costs, as a share of the army — and it is
 * charged IN PROPORTION TO HOW MUCH OF THE CROWD WAS STANDING IN IT.
 *
 * A flat penalty for being clipped would be the wrong shape twice over: it would
 * make a graze as bad as a direct hit, and it would let a 500-strong army — five
 * metres wide, most of a carriageway — take the same hit as a squad of six that
 * genuinely fitted in the gap. Charging by overlap means a big crowd cannot
 * fully dodge anything, which is a real and welcome pressure at scale: growth
 * buys firepower and costs agility.
 */
const BOSS_HIT_SHARE = 0.2;

boss.onStrike((_kind, zx, half) => {
  // Same mercy threshold breaches use: a tiny army cannot kill a boss and must
  // not be put in a spiral by one.
  if (world.troops < MERCY_TROOPS) return;
  const crowd = Math.max(0.4, world.squadHalfWidth);
  const lo = Math.max(zx - half, world.squadCenter.x - crowd);
  const hi = Math.min(zx + half, world.squadCenter.x + crowd);
  const covered = Math.max(0, hi - lo) / (crowd * 2);
  if (covered <= 0) return;
  // Scaled by how much of the crowd was actually in it, so a graze is a nudge
  // and a direct hit is a jolt.
  zoom.shake(SHAKE_BOSS_HIT * covered);
  // Thrown AWAY from where the blow landed, and downward — a hit from the left
  // shoves the frame right. The sign is what turns a rattle into a direction.
  zoom.punch(world.squadCenter.x - zx, -0.3, PUNCH_BOSS_HIT * covered);
  hitStop(Math.round(HITSTOP_BOSS_HIT * covered));
  payTroops(-Math.max(1, Math.round(world.troops * BOSS_HIT_SHARE * covered)));
});

/** A hive does not hit you; it hatches. The walkers are ordinary walkers, spawned
 *  through the enemy module — two entity modules must not import each other, so
 *  the boss asks and this file does it. */
/**
 * A BOSS THAT BREAKS THROUGH STILL COUNTS AGAINST THE RUSH.
 *
 * It cost the army a fifth of itself on the way past, which is payment enough.
 * Requiring a kill would let a weak army stall forever in front of a boss it
 * cannot beat, on a level it can then never finish — the level has to terminate
 * whatever happens, and the punishment is the blood, not the deadlock.
 */
boss.onEscaped(() => {
  rushBossFinished();
});

boss.onHatch((lane, z, count) => {
  enemies.spawnPack(lane, z, count, walkerHp());
});

/**
 * Killing a boss pays TROOPS AND A WEAPON, and the weapon is the point.
 *
 * Troops alone would make a boss a large barrel. A crew of rocketeers is a
 * permanent change to what the army is, visible on the soldiers themselves and
 * in the stream they fire, so the run is measurably different after the fight
 * than before it — which is what an event is supposed to leave behind.
 *
 * Matched to the fight so the reward reads as spoils: the brute swings a club
 * and leaves launchers, the roller is a machine and leaves miniguns, the hive
 * leaves the bodies it was going to spawn.
 */
const BOSS_REWARD_SHARE = 0.3;
const BOSS_REWARD_FLOOR = 12;
const BOSS_CREW = 2;

/**
 * How hard the camera is knocked, in metres of peak offset.
 *
 * Only two events in the game get one, and that is the whole design: a shake
 * spent on something that happens every few seconds stops meaning anything. A
 * boss dying and a boss landing a hit on you are the two moments where the
 * screen should move.
 */
const SHAKE_BOSS_DEATH = 0.6;
const SHAKE_BOSS_HIT = 0.42;

/**
 * PUNCH — the same two events, plus the one that hurts, given a direction.
 *
 * Peak offsets are smaller than the shakes they accompany because a punch is a
 * sustained displacement rather than an oscillation: 0.35 m of lurch reads much
 * larger than 0.35 m of wobble. Paired with the shake rather than replacing it —
 * the shake is the rattle, the punch is the shove.
 */
const PUNCH_BOSS_DEATH = 0.38;
const PUNCH_BOSS_HIT = 0.3;
const PUNCH_BREACH = 0.14;

/**
 * HIT-STOP — sim frames dropped on impact, and why they are DROPPED rather than
 * slowed.
 *
 * The standard trick is to scale `dt` down for a moment. This project cannot:
 * core/loop.ts advances the sim at exactly 60 Hz precisely so that a run pays
 * the same on a 120 Hz iPad as on a throttled Android, and a variable `dt` gives
 * that up for a visual effect. Skipping whole steps keeps every step exactly
 * 1/60 s, so the sim stays reproducible and the frames in between simply repeat.
 *
 * Which is also what hit-stop actually IS. The brain reads a freeze at the
 * moment of contact as "that landed"; it does not read a slowdown, it reads a
 * slowdown as slow motion. Three to five frames is the whole window — past about
 * eight the road visibly stops and it turns into a stutter.
 *
 * Only impacts ON THE PLAYER and the death of a boss get one. A barrel going off
 * happens several times a corridor, and a game that freezes several times a
 * corridor is a game that is dropping frames.
 */
const HITSTOP_BOSS_DEATH = 5;
const HITSTOP_BOSS_HIT = 4;
const HITSTOP_BREACH = 3;
/** Weight of a breach — bodies × kind cost — that earns a freeze. A stray walker
 *  does not; a pack arriving intact, an elite or an ogre does. */
const HITSTOP_BREACH_WEIGHT = 6;

let hitStopTicks = 0;

/** Freeze the sim for `ticks` steps. Takes the larger of the current freeze and
 *  this one, so two things landing together cannot stack into a stall. */
function hitStop(ticks: number): void {
  if (ticks > hitStopTicks) hitStopTicks = ticks;
}

/** Blasts thrown around a dying boss, and how far out they scatter. */
const BOSS_DEATH_BLASTS = 7;
const BOSS_DEATH_SPREAD = 2.4;
const BOSS_DEATH_FLASH = 5.5;
/** Seconds between them. Seven of these fill 0.72 s, just inside the boss's own
 *  death animation, so the last blast lands as the wreck hits the deck. */
const BOSS_BLAST_GAP = 0.12;
let blastsLeft = 0;
let blastTimer = 0;
let blastX = 0;
let blastZ = 0;

boss.onKilled((kind) => {
  // A boss that just fell over was the least explosive thing in a game whose
  // barrels detonate. It comes apart in a string of blasts up its own body, and
  // the camera takes the hit.
  zoom.shake(SHAKE_BOSS_DEATH);
  zoom.punch(world.squadCenter.x - boss.x, -0.25, PUNCH_BOSS_DEATH);
  hitStop(HITSTOP_BOSS_DEATH);
  // Staggered, not simultaneous. Seven blasts in one frame is one big flash;
  // seven blasts walking up the body over three quarters of a second is a boss
  // coming apart, and the death animation lasts long enough to carry it.
  blastsLeft = BOSS_DEATH_BLASTS;
  blastTimer = 0;
  blastX = boss.x;
  blastZ = boss.z;
  payTroops(Math.max(BOSS_REWARD_FLOOR, Math.round(world.troops * BOSS_REWARD_SHARE)));
  commander.say("boss");
  bossesKilled++;
  rushBossFinished();
  if (kind === "brute") world.rocketeers += ROCKET_CREW * BOSS_CREW;
  else if (kind === "roller") world.gunners += MINIGUN_CREW * BOSS_CREW;
  else world.elites = Math.min(world.troops, world.elites + RECRUIT_ELITES * 3);
  armCarriers();
});

ally.onFreed(() => {
  commander.say("ally");
  // The same beat a boss death gets, at half the size: it is the second biggest
  // thing that can happen on the road, and it should land like it.
  zoom.shake(SHAKE_BOSS_DEATH * 0.5);
  zoom.punch(0, -1, PUNCH_BOSS_DEATH * 0.5);
  hitStop(HITSTOP_BOSS_DEATH);
});

// Nothing to show until one arrives.
bossBar.hide();

/* ── Levels: an approach, then a boss rush ────────────────────────────────
 *
 * A LEVEL BUILDS TO SOMETHING. The first version counted two boss kills and let
 * the conductor scatter the bosses through the level on a distance cadence,
 * which made them punctuation rather than a destination — Mischa's note was
 * exact: "it doesn't look like we have a big boss and horde moment that you have
 * to beat that culminates the level."
 *
 * So a level is two phases:
 *
 *   APPROACH  `levelDistance()` metres of corridor. Gates, barrels and whatever
 *             enemies this level has unlocked. No bosses at all. This is where
 *             the army is built, and it is the only place it can be built.
 *   RUSH      The corridor stops. A warning fires, the road empties, and then
 *             the level's bosses arrive back to back, each with a horde, with
 *             only a couple of seconds between them. Beat them and the level is
 *             over.
 *
 * Then the card, an upgrade, and the next level from scratch — a bigger start,
 * a harder corridor, an enemy you have not seen, and one more boss at the end.
 */

/**
 * The escort that walks in with every boss of the rush.
 *
 * THE HORDE IS THE SHARPEST DIFFICULTY LEVER IN THE GAME and it is worth saying
 * so where it is defined: four packs at level one measured 5 wipes in 32 with
 * under a third of runs clearing a level; two packs measured 2 wipes with nearly
 * half clearing. Three, growing to six by level four. Re-measure after touching
 * it — nothing else moves the rate this far this fast.
 */
const HORDE_PACKS = 4;
const HORDE_PACK_SIZE = 12;
/** Metres behind the boss the escort forms up. Far enough that they are visible
 *  around it rather than inside it. */
const HORDE_DEPTH = 5;

/** Metres of approach before the rush, and how much each level adds. At 6 m/s
 *  level one builds for about fifty seconds before the horns. */
const LEVEL_DISTANCE = 300;
const LEVEL_DISTANCE_STEP = 40;

/** Seconds of empty road between the warning and the first boss, and between
 *  one boss and the next. The first is a breath; the second is barely one. */
const RUSH_LEAD = 2.6;
const RUSH_BETWEEN = 1.4;

function levelDistance(): number {
  return LEVEL_DISTANCE + (world.level - 1) * LEVEL_DISTANCE_STEP;
}

/** How many bosses end this level. One on level one — the level that teaches
 *  what a boss is — then two, then three, capped at four. */
function rushSize(): number {
  return Math.min(4, 1 + Math.floor(world.level / 2));
}

/**
 * Seconds of difficulty a completed level is worth.
 *
 * `PENALTY_BANDS` and `ROW_WIDTHS` in mechanics/gates.ts are indexed off elapsed
 * time in 25-second tiers. Rather than rewrite both tables to take a level — a
 * change that would invalidate every measurement this project has made — a level
 * ADDS to the elapsed time the composer is told about. Level 3 therefore opens
 * at the difficulty level 1 reached after a minute, which is the "harder from
 * its first second" property that was missing, with none of the tuning thrown
 * away. The BEAT table gates itself separately, by `unlock` (director.ts).
 */
const LEVEL_DIFFICULTY_SECONDS = 25;
/** Troops a level hands you for each one already cleared. */
const LEVEL_START_TROOPS = 8;

function difficultyClock(): number {
  return world.elapsed + (world.level - 1) * LEVEL_DIFFICULTY_SECONDS;
}

/* --- perks: the upgrade path ------------------------------------------- */

/**
 * PERKS ARE PERMANENT AND THEY STACK, and the POOL GROWS. Three ranks of the
 * same three things is a clear upgrade path and a shallow one; a level that
 * offers something you have not been offered before is the other half of "each
 * level introduces new upgrades". Two of the five change how the game is played
 * rather than how big it starts, and they unlock late enough that the player has
 * met the ability they modify.
 */
type PerkId = "men" | "rate" | "power" | "medic" | "salvage";
const perks: Record<PerkId, number> = { men: 0, rate: 0, power: 0, medic: 0, salvage: 0 };

const PERK_MEN_TROOPS = 6;
const PERK_RATE_CREW = 3;
const PERK_POWER_CREW = 2;
/**
 * Share of every casualty each rank of MEDIC walks off, and the cap.
 *
 * The cap is the whole design of this perk. Losses are the only thing that ends
 * a run, so a defensive perk with no ceiling is the one the player takes three
 * times and stops being able to lose; at 0.45 even a fully-ranked medic still
 * pays more than half of every red segment and a boss breakthrough still hurts.
 */
const PERK_MEDIC_SAVE = 0.14;
const PERK_MEDIC_CAP = 0.45;
/** Extra share a rank of SALVAGE adds to what a broken barrel pays. Applies to
 *  the barrel's own payout only, not to the prize riding it — a crate of
 *  rocketeers is worth the same however good your scavengers are. */
const PERK_SALVAGE_SHARE = 0.3;

/** What a given rank of MEDIC is worth, capped. Exported to the perk card so the
 *  text on the card is the number the game actually applies. */
function medicSave(rank: number): number {
  return Math.min(PERK_MEDIC_CAP, rank * PERK_MEDIC_SAVE);
}

const ROMAN = ["", "I", "II", "III", "IV", "V", "VI", "VII", "VIII", "IX", "X"];

interface PerkSpec {
  id: PerkId;
  icon: string;
  title: string;
  unlock: number;
  detail: (rank: number) => string;
}

const PERK_SPECS: readonly PerkSpec[] = [
  {
    id: "men",
    icon: "🎖",
    title: "REINFORCEMENTS",
    unlock: 1,
    detail: (r) => `Start each level with ${r * PERK_MEN_TROOPS} extra troops`,
  },
  {
    id: "rate",
    icon: "🔫",
    title: "MINIGUN CREW",
    unlock: 1,
    detail: (r) => `Start each level with ${r * PERK_RATE_CREW} gunners`,
  },
  {
    id: "power",
    icon: "🚀",
    title: "ROCKET CREW",
    unlock: 1,
    detail: (r) => `Start each level with ${r * PERK_POWER_CREW} rocketeers`,
  },
  {
    id: "medic",
    icon: "🚑",
    title: "FIELD MEDICS",
    unlock: 3,
    detail: (r) => `Walk off ${Math.round(medicSave(r) * 100)}% of every casualty`,
  },
  {
    id: "salvage",
    icon: "🧲",
    title: "SALVAGE CREW",
    unlock: 4,
    detail: (r) => `Barrels pay ${Math.round(r * PERK_SALVAGE_SHARE * 100)}% more`,
  },
];

/** Which three to offer. The unlocked pool rotated by level, so a new perk is
 *  always on the card the level it unlocks rather than whenever the dice say. */
function perkOffers(): PerkOffer[] {
  const pool = PERK_SPECS.filter((p) => p.unlock <= world.level);
  const fresh = pool.filter((p) => p.unlock === world.level);
  const rest = pool.filter((p) => p.unlock !== world.level);
  const chosen = [...fresh, ...rest].slice(0, 3);
  return chosen.map((p) => {
    const rank = perks[p.id] + 1;
    return {
      id: p.id,
      icon: p.icon,
      title: p.title,
      rank: ROMAN[Math.min(rank, ROMAN.length - 1)] ?? "",
      detail: p.detail(rank),
    };
  });
}

/* --- level state -------------------------------------------------------- */

type LevelPhase = "approach" | "rush";
let phase: LevelPhase = "approach";
let approached = 0;
let rushLeft = 0;
let rushTimer = 0;
let bossesKilled = 0;
let biggestCrowd = 0;
/** True while the autopilot is driving. A wipe then restarts silently instead of
 *  putting up a screen that nothing is going to press. */
let harnessMode = false;

const TEASERS: readonly string[] = [
  "Level %L brings a bigger horde",
  "Level %L: they have been waiting for you",
  "Level %L brings heavier company",
  "Level %L. The bridge does not get shorter",
];

/** The approach is over. Clear the road and sound the horns. */
function beginRush(): void {
  phase = "rush";
  rushLeft = rushSize();
  rushTimer = RUSH_LEAD;
  gates.reset();
  barrels.clear();
  commander.say("rush");
  levelCard.banner(rushLeft > 1 ? "BOSS RUSH" : "BOSS INCOMING");
}

/** One boss of the rush is done with — killed or broken through. */
function rushBossFinished(): void {
  if (phase !== "rush") return;
  if (rushLeft > 0) rushTimer = RUSH_BETWEEN;
  else clearLevel();
}

function clearLevel(): void {
  // The harness plays whole runs with nobody to press a button. It takes the
  // level and the perk it would most likely have picked, and carries on — a
  // paused game would otherwise silently eat the rest of a 110-second sample and
  // report it as a run that stalled.
  if (harnessMode) {
    world.level++;
    perks.men++;
    resetRun();
    return;
  }
  state.transition("paused");
  // The bar belongs to a boss that is now dead, and the tick that would have
  // hidden it does not run while the game is paused.
  bossBar.hide();
  bossShowing = false;
  ui.classList.add("is-carded");
  const next = world.level + 1;
  levelCard.showCleared(
    {
      level: world.level,
      biggestCrowd,
      bosses: bossesKilled,
      teaser: (TEASERS[(next - 2) % TEASERS.length] ?? "").replace("%L", String(next)),
    },
    perkOffers(),
  );
}

function failLevel(): void {
  state.transition("paused");
  bossBar.hide();
  bossShowing = false;
  ui.classList.add("is-carded");
  levelCard.showFailed(world.level);
}

levelCard.onChoice((choice) => {
  if (choice.kind === "perk") {
    const id = choice.id as PerkId;
    if (id in perks) perks[id]++;
    world.level++;
  } else if (choice.kind === "restart") {
    for (const k of Object.keys(perks) as PerkId[]) perks[k] = 0;
    world.level = 1;
  }
  // "retry" keeps both the level and the perks: losing a level should cost the
  // level, not the run's whole investment. That is the difference between a
  // setback and a punishment.
  ui.classList.remove("is-carded");
  state.transition("running");
  resetRun();
});

// ── Content pacing ─────────────────────────────────────────────────────────

/**
 * A BARREL CLUSTER IS A PLACE, NOT A ROW.
 *
 * Three barrels used to be spread across the lanes at ±0.62 and 0, which on the
 * old narrow road meant they covered it end to end — the same "everything is a
 * wall" problem gate rows had. They are now a tight group of three placed
 * somewhere on the road, so shooting them is a DETOUR: you go to where they are,
 * which is a second or so of not being anywhere else.
 */
const BARREL_SPACING = 1.95;
const BARREL_CLUSTER = 3;
/** A barrel's own width, for fitting a cluster into a gap. */
const BARREL_FACE = 1.7;
/** Metres of daylight kept between a compound placement's two halves, so they
 *  read as two options rather than as one cluttered obstacle. */
const CLUSTER_CLEARANCE = 0.5;
/**
 * Segments a compound placement's gate row may have.
 *
 * Two, so the row covers 42% of the road and there is genuinely somewhere else
 * to be. The whole premise of a blockade or a crossroads is two options on one
 * plane; a four-wide row covers 84% and there is no second option left to place.
 */
const COMPOUND_SEGMENTS = 2;
/** Metres of road a cluster or a pack keeps clear of the kerb, so nothing ever
 *  spawns half on the grass. */
const PLACE_INSET = 1.3;
const SPAWN_Z = -58;
let rowIndex = 0;
/** Suspends the director. Only the dev calibration harness touches this — a
 *  probe needs an empty corridor or the traffic eats the rounds it is counting. */
let contentSpawning = true;

/**
 * THE CONDUCTOR. One cursor decides everything that enters the corridor, so two
 * things can no longer land on the same plane — see mechanics/director.ts for
 * what this replaced and why three independent timers could not be made to fit.
 */
const director = createDirector();
/** Reused every gate placement so the corridor never allocates inside tick(). */
const gateBuffer: number[] = [0, 0, 0, 0];
/** The director's own stream, kept apart from the gate module's so that tuning
 *  gate variety cannot silently reshuffle the corridor's pacing. */
const rowRng = createRng(0xc0ffee);
/**
 * True when the last gate row carried no blue at all.
 *
 * Never two dry rows running: a stretch of pure loss with nothing to steer
 * toward does not read as difficulty, it reads as the game having stopped
 * offering anything. One all-red row is a "pick your loss" moment — the
 * reference has them — and two in a row is just the road taking troops off you.
 */
let lastRowWasDry = false;

/** Compose a row into `gateBuffer`, honouring the no-two-dry-rows rule. */
function composeRow(maxCount?: number): number {
  const count = composeAutoRow(
    rowRng,
    difficultyClock(),
    world.troops,
    gateBuffer,
    maxCount,
    lastRowWasDry,
  );
  lastRowWasDry = !rowHasReward(gateBuffer, count);
  return count;
}

/**
 * What the next barrel carries.
 *
 * Recruits are the common case, because crowd size is the run's main currency
 * and a weapon on every barrel would make firepower the only thing that
 * mattered. Weapons are the treat: rarer, and worth steering for.
 */
const PICKUP_ODDS: readonly PickupKind[] = [
  "recruit",
  "recruit",
  "recruit",
  "minigun",
  "recruit",
  "flamer",
  "recruit",
  "recruit",
  "rocket",
  "recruit",
  "freezer",
];
let pickupCursor = 0;

function pickForRow(): PickupKind {
  // Walked rather than rolled, so a run cannot go two minutes without a weapon
  // on a bad streak of luck — the same reason gate rows are authored.
  const kind = PICKUP_ODDS[pickupCursor % PICKUP_ODDS.length]!;
  pickupCursor++;
  return kind;
}

/** How much road the cage occupies, for `clusterX`. Matches CAGE_HALF_WIDTH in
 *  entities/ally.ts with a little clearance, so a cage placed at the kerb does
 *  not overhang it. */
const CAGE_WIDTH = 2.6;

/** Walker toughness tracks the army, for the reason in mechanics/pacing.ts. The
 *  floor is the old fixed value, so the opening is unchanged. Barrels no longer
 *  carry elites at all — they carry pickups — so only walkers need this. */
function walkerHp(): number {
  return enemyHp(world.troops, tierFor(world.troops), bullets.tuning, squad.radiusX, WALKER_PASS_SHARE, 4);
}

/**
 * What a cage costs to open.
 *
 * Priced off the same curve as an ogre and then softened, because the two are
 * doing different jobs: an ogre is meant to be walked around at low strength,
 * and a cage is meant to be OPENED. A prize nobody can afford is not a prize.
 * The floor is high for the opposite reason — free is not a decision either.
 */
function cageHp(): number {
  return enemyHp(
    world.troops,
    tierFor(world.troops),
    bullets.tuning,
    squad.radiusX,
    CAGE_PASS_SHARE,
    60,
  );
}

/** Heavies and bikers are single bodies rather than a pack of eight, so each one
 *  soaks a much larger share of a pass — see ELITE_PASS_SHARE in pacing.ts. */
function eliteHp(): number {
  return enemyHp(world.troops, tierFor(world.troops), bullets.tuning, squad.radiusX, ELITE_PASS_SHARE, 30);
}

/** An OGRE soaks most of a whole approach — see OGRE_PASS_SHARE. The floor is
 *  high because an ogre met early must still be a wall rather than a speed bump;
 *  it is meant to be walked around at low strength, not shot down. */
function ogreHp(): number {
  return enemyHp(
    world.troops,
    tierFor(world.troops),
    bullets.tuning,
    squad.radiusX,
    OGRE_PASS_SHARE,
    90,
  );
}

/** A biker arrives sooner, so it gets less of the approach to be shot at and is
 *  priced accordingly. Two thirds of a heavy. */
function bikerHp(): number {
  return Math.max(20, Math.round(eliteHp() * 0.66));
}

const ELITE_SQUAD = 3;
const ELITE_SPACING = 1.8;
const BIKER_SQUAD = 2;
const BIKER_SPACING = 2.4;

/**
 * A world X for a cluster of `width` metres, biased toward `side` when the
 * director wants this lined up with something. Always leaves `PLACE_INSET` of
 * road outside it so nothing straddles a kerb.
 */
function clusterX(width: number, side: number): number {
  const reach = Math.max(0, CORRIDOR_HALF_WIDTH - width / 2 - PLACE_INSET);
  if (reach <= 0) return 0;
  const dir = side !== 0 ? Math.sign(side) : rowRng() < 0.5 ? -1 : 1;
  return dir * reach * (0.35 + rowRng() * 0.65);
}

/**
 * Centre a cluster of at most `max` items inside the stretch of road between
 * `from` and `to`, dropping items until it fits. Returns the centre and how many
 * survived; 0 means the stretch is too narrow for even one.
 *
 * THIS IS THE FIX FOR THE FLOATING GOLD OBJECTS. A compound placement put its
 * barrel cluster on "the other side" of the gate row, which is not the same
 * thing as "in the road the gate row left over": a 5.6 m cluster placed against
 * the far kerb still overlapped a row reaching past the centreline, so barrels
 * ended up INSIDE a gate panel — hidden by it, while the pickup they were
 * carrying hovered above the barrier in its gold ring with nothing under it.
 * Reported, reasonably, as floating artifacts.
 */
function fitCluster(
  from: number,
  to: number,
  max: number,
  spacing: number,
  itemWidth: number,
): { x: number; count: number } {
  const lo = Math.min(from, to);
  const hi = Math.max(from, to);
  const room = hi - lo;
  let count = max;
  while (count > 1 && (count - 1) * spacing + itemWidth > room) count--;
  if ((count - 1) * spacing + itemWidth > room) return { x: 0, count: 0 };
  return { x: (lo + hi) / 2, count };
}

/**
 * `side` is the director's lateral request: −1 left, +1 right, 0 free. It is how
 * a guard gets lined up with the prize it is guarding — see mechanics/director.ts.
 */
function place(what: Placement, z: number, side = 0, free?: { x: number; count: number }): void {
  // WHILE A BOSS HOLDS ONE KERB, EVERYTHING ELSE GOES TO THE OTHER ONE. This is
  // the line that turns a boss from an obstacle into a decision: the prizes keep
  // coming, they are all on the side the boss is not standing on, and the guns
  // only point one way. Without it the corridor would drop barrels inside the
  // boss and the fight would be a shooting gallery with one target.
  if (what !== "boss" && boss.fighting) {
    side = -boss.side;
    // A compound placement is itself an either/or spanning both kerbs, and the
    // boss has already taken one of them — there is no road left for the second
    // half. The prize half is what is worth keeping.
    if (what === "blockade" || what === "crossroads") what = "barrels";
  }

  switch (what) {
    case "boss": {
      // Never two at once. The director cannot produce that on its own, but a
      // dev harness spawn during an encounter could.
      if (boss.active) return;
      const kind = BOSS_ORDER[bossIndex % BOSS_ORDER.length]!;
      const dir = side !== 0 ? Math.sign(side) : 1;
      boss.spawn(kind, bossHp(kind, bossIndex), dir, z);
      bossBar.reset(boss.maxHp);
      bossIndex++;

      // A BOSS ARRIVES WITH A HORDE. In the reference the giant is backed by a
      // mass of small enemies filling the road behind it, and that is most of
      // why the moment reads as an army meeting an army rather than as one big
      // health bar. They spawn BEHIND the boss so they walk out from around it,
      // and on the kerb it is not holding — the boss owns one side, its escort
      // owns the rest, and between them the road is genuinely contested.
      const packs = HORDE_PACKS + Math.min(3, world.level - 1);
      // Three packs at level one, six by level four. THE HORDE IS NOW THE
      // DOMINANT DIFFICULTY LEVER and it is a sharp one: four packs at level one
      // measured 5 wipes in 32 with under a third of runs clearing a level, and
      // two packs measured 2 wipes with nearly half clearing. Re-measure after
      // touching it; nothing else in the game moves the rate this far this fast.
      for (let i = 0; i < packs; i++) {
        const spread = ((i + 0.5) / packs) * 2 - 1;
        enemies.spawnPack(
          clamp(-dir * 0.35 + spread * 0.65, -0.9, 0.9),
          z - HORDE_DEPTH - i * 1.6,
          HORDE_PACK_SIZE,
          walkerHp(),
        );
      }
      return;
    }

    case "gate": {
      const count = composeRow();
      gates.spawnRow(gateBuffer, z, rowPlacement(rowRng, count, side));
      return;
    }

    case "barrels": {
      rowIndex++;
      const hp = barrelHp(
        rowIndex,
        world.troops,
        tierFor(world.troops),
        bullets.tuning,
        squad.radiusX,
      );
      const width = (BARREL_CLUSTER - 1) * BARREL_SPACING + BARREL_FACE;
      const cx = free ? free.x : clusterX(width, side);
      const n = free ? free.count : BARREL_CLUSTER;
      for (let i = 0; i < n; i++) {
        const x = cx + (i - (n - 1) / 2) * BARREL_SPACING;
        const lane = x / CORRIDOR_HALF_WIDTH;
        const rider = pickups.spawn(pickForRow(), x, barrels.riderY, z);
        // If the barrel pool is full there is nothing for the prize to sit on,
        // and an unpinned pickup is a gold-ringed object hovering over the road.
        if (barrels.spawn(lane, z, hp, rider) < 0 && rider >= 0) pickups.retire(rider);
      }
      return;
    }

    case "walkers": {
      // A pack is ~2 m across, so it is a thing standing in one part of the road
      // rather than a line across it. That is what lets it guard an approach.
      const lane = clusterX(2.2, side) / CORRIDOR_HALF_WIDTH;
      enemies.spawnPack(lane, z, 8, walkerHp());
      return;
    }

    // BUILT SINCE THE ENEMY MODULE WAS WRITTEN AND NEVER ONCE SPAWNED. The
    // orchestrator only ever placed walker packs, so two finished, tested enemy
    // types sat unreachable behind an unused entry point — the cheapest content
    // in the project, for the cost of a case each.

    case "elites": {
      // Gold-rimmed heavies, a short line of them. They hold their ground rather
      // than rushing, so they are cover you have to chew through.
      const width = (ELITE_SQUAD - 1) * ELITE_SPACING + 1;
      const cx = clusterX(width, side);
      for (let i = 0; i < ELITE_SQUAD; i++) {
        const x = cx + (i - (ELITE_SQUAD - 1) / 2) * ELITE_SPACING;
        enemies.spawnElite(x / CORRIDOR_HALF_WIDTH, z, eliteHp(), false);
      }
      return;
    }

    case "cage": {
      // PRICED LIKE A HEAVY, NOT LIKE A BARREL. An ally that opens in a second
      // is a free ally, and this is the biggest single thing the corridor gives
      // away — it should cost roughly what an ogre costs, which is most of an
      // approach's fire. If the module refuses (a cage is already up, or the
      // robot is already out) nothing is placed and the beat is quiet road,
      // which is the correct outcome: there is only one of these per level.
      ally.spawnCage(clusterX(CAGE_WIDTH, side), z, cageHp());
      return;
    }

    case "ogres": {
      // ONE BODY, and that is the design. A line of ogres is a wall, which the
      // gate rows already do better; a single one standing in front of something
      // you want is a question about whether the prize is worth the approach.
      enemies.spawnOgre(clusterX(1.8, side) / CORRIDOR_HALF_WIDTH, z, ogreHp());
      return;
    }

    case "bikers": {
      // Fast. A biker closes at more than four times a walker's pace, which is
      // what makes it the one enemy that punishes reacting late rather than
      // choosing wrong.
      const width = (BIKER_SQUAD - 1) * BIKER_SPACING + 1.4;
      const cx = clusterX(width, side);
      for (let i = 0; i < BIKER_SQUAD; i++) {
        const x = cx + (i - (BIKER_SQUAD - 1) / 2) * BIKER_SPACING;
        enemies.spawnElite(x / CORRIDOR_HALF_WIDTH, z, bikerHp(), true);
      }
      return;
    }

    // ---- compound placements: two things on ONE plane, on opposite sides ----
    //
    // Everything above is sequential, and sequential content cannot force a
    // choice: the crowd only has to be in one place at the moment each row
    // arrives, so it can take the best of every row in turn. These two put both
    // options on the same plane, where taking one is not taking the other.

    case "blockade": {
      // The gate takes one side; the walkers stand in the gap on the other, so
      // the clear road is not clear. Pay the row, or fight through the bodies.
      const dir = side !== 0 ? Math.sign(side) : rowRng() < 0.5 ? -1 : 1;
      const count = composeRow(COMPOUND_SEGMENTS);
      const placed = rowPlacement(rowRng, count, dir);
      gates.spawnRow(gateBuffer, z, placed);
      // Middle of whatever road the row left over, so the pack stands squarely
      // in the way of the dodge rather than off beside it.
      const inner = placed.centerX - dir * placed.halfSpan;
      enemies.spawnPack(
        (-dir * CORRIDOR_HALF_WIDTH + inner) / 2 / CORRIDOR_HALF_WIDTH,
        z,
        8,
        walkerHp(),
      );
      return;
    }

    case "crossroads": {
      // Bodies or firepower. The row pays troops, the barrels carry a pickup,
      // and they are on opposite kerbs — this is the beat that teaches that a
      // run heavy in one axis and empty in the other is a choice you made.
      const dir = side !== 0 ? Math.sign(side) : rowRng() < 0.5 ? -1 : 1;
      const count = composeRow(COMPOUND_SEGMENTS);
      const placed = rowPlacement(rowRng, count, dir);
      gates.spawnRow(gateBuffer, z, placed);
      // Into the road the ROW left over, not merely onto the far kerb — see
      // fitCluster. A cluster that does not fit sheds barrels rather than
      // straddling the barrier.
      const inner = placed.centerX - dir * placed.halfSpan;
      const fit = fitCluster(
        -dir * (CORRIDOR_HALF_WIDTH - PLACE_INSET),
        inner - dir * CLUSTER_CLEARANCE,
        BARREL_CLUSTER,
        BARREL_SPACING,
        BARREL_FACE,
      );
      if (fit.count > 0) place("barrels", z, -dir, fit);
      return;
    }
  }
}

/**
 * Metres of road the corridor is pre-filled with at the start of a run.
 *
 * The gate module used to prime three rows itself so the run never opened on an
 * empty road (`frame_000` has two rows already stacked). That went with its
 * auto-spawn, so the director has to do it — and doing it by REPLAYING the
 * director means the opening layout obeys exactly the same spacing rule as
 * every metre after it, rather than being a hand-placed special case that can
 * drift away from the real thing.
 *
 * 40 m leaves the nearest placement ~18 m out, about three seconds at the
 * default speed: enough to read the first decision before having to make it.
 */
const PRIME_SPAN = 40;

/**
 * Fill the visible corridor as if the run had already been going for
 * PRIME_SPAN metres. An item placed `d` metres into that replay has had
 * `PRIME_SPAN - d` metres to travel, which is exactly where it is put.
 */
function primeCorridor(): void {
  const step = 0.5;
  for (let d = 0; d < PRIME_SPAN; d += step) {
    const due = director.advance(step);
    if (due) place(due.what, SPAWN_Z + (PRIME_SPAN - d), due.side);
  }
}

/**
 * ONE SOLDIER, as the reference opens. This was 8 only because gate rows were
 * rolled independently and roughly one in four came up all-red — an unavoidable
 * death at low strength. `MERCY_TROOPS` in mechanics/gates.ts now guarantees a
 * blue segment and mild penalties until the squad can absorb a bad row, so the
 * opening beat the reference actually has is available again: one man on an
 * empty road, and the first gate is the whole game in miniature.
 */
const START_TROOPS = 1;

/** Runs lost since boot. The failure-rate target is a ratio of runs, so it has
 *  to be counted rather than inferred from a troop curve. Read by the autopilot. */
let wipes = 0;

/**
 * Zero troops is a loss. Restarting immediately (rather than freezing on an
 * empty road) keeps the prototype iterable — there is no debrief screen yet,
 * and a frozen screen teaches nobody anything.
 */
/**
 * Army sizes the Commander files a report on. Powers-of-ish rather than round
 * hundreds, so the remarks thin out as the run goes on instead of arriving at a
 * constant rate through the part of the run that is already loudest.
 */
const GROWTH_MARKS: readonly number[] = [25, 80, 200, 450, 900];
let growthMark = 0;

/** Called after every payout. Cheap: an integer compare in the common frame. */
function noteGrowth(): void {
  while (growthMark < GROWTH_MARKS.length && world.troops >= GROWTH_MARKS[growthMark]!) {
    growthMark++;
    commander.say("growth");
  }
}

function resetRun(): void {
  // A level STARTS here, so this is where the upgrade path is spent: perks are
  // permanent and they are applied fresh every level rather than accumulated on
  // the army, which keeps "what do I start with" a single readable expression.
  // LEVEL 1 STILL OPENS ON ONE MAN. That beat is the reference's and it is the
  // best thirty seconds the game has; it survives untouched at level 1 with no
  // perks. Every level after it starts bigger, because the difficulty bands do
  // too — and because a crew perk is worthless at one troop, where `armCarriers`
  // correctly clamps three gunners down to one. The upgrade path needs bodies to
  // hand the equipment to.
  world.troops = START_TROOPS + perks.men * PERK_MEN_TROOPS + (world.level - 1) * LEVEL_START_TROOPS;
  bossesKilled = 0;
  biggestCrowd = world.troops;
  phase = "approach";
  approached = 0;
  rushLeft = 0;
  rushTimer = 0;
  director.setLevel(world.level);
  // The difficulty clock is per LEVEL, not per session: `difficultyClock()` adds
  // the level's own offset on top, so leaving elapsed running would compound the
  // two and make level 3 open where level 6 should.
  world.elapsed = 0;
  world.health = 1;
  barrels.clear();
  enemies.clear();
  boss.clear();
  blastsLeft = 0;
  bossBar.hide();
  bossShowing = false;
  bossIndex = 0;
  gates.reset();
  director.reset();
  ally.clear();
  hitStopTicks = 0;
  rowIndex = 0;
  pickups.clear();
  pickupCursor = 0;
  lastRowWasDry = false;
  world.elites = 0;
  world.gunners = perks.rate * PERK_RATE_CREW;
  world.rocketeers = perks.power * PERK_POWER_CREW;
  streak.reset();
  growthMark = 0;
  // Not `reset()` on the commander: his line cursors are the only thing that
  // stops a player hearing "Casualties acceptable" four runs running, and a run
  // ending is exactly when they should NOT be rewound.
  commander.say("wipe");
  armCarriers();
  primeCorridor();
  zoom.reset(world.troops);
  world.zoom = zoom.distance;
}

/**
 * Muzzle scratch buffer, allocated once. One entry per possible stream: the
 * squad reports where each soldier's rifle actually is, and bullets fires one
 * stream from each, which is what makes "20 troops = 20 streams" true by
 * construction rather than by a rate curve pretending to be density.
 */
const shooters = Array.from({ length: MAX_STREAMS }, () => new THREE.Vector3());
/** Parallel to `shooters`: what each of them is carrying. Allocated once. */
const shooterKinds = new Uint8Array(MAX_STREAMS);

/** Hoisted so the per-tick rider seating allocates nothing. */
function seatRider(_id: number, tag: number, x: number, topY: number, z: number): void {
  if (tag >= 0) pickups.pin(tag, x, topY, z);
}

// ── Collision ──────────────────────────────────────────────────────────────

/**
 * A ROCKET EXPLODES.
 *
 * The chain Mischa asked for, end to end: shoot a barrel carrying a launcher →
 * some of your soldiers visibly carry launchers → their streams fire visible
 * rockets → the rockets BLOW UP where they land and take out whatever was
 * standing next to the thing they hit. Each link is something you can see, and
 * the last one is the payoff — a single rocket clearing three barrels at once is
 * the sort of thing you did not plan and want to show someone.
 *
 * Splash is applied by re-running the same hit tests at a wide pad. Each call
 * finds the nearest remaining target, so `SPLASH_TARGETS` passes reach that many
 * separate things; it is a cheap stand-in for a real radius query and it is
 * exactly as accurate at this scale.
 */
const SPLASH_RADIUS = 2.6;
const SPLASH_DAMAGE = 0.55;
const SPLASH_TARGETS = 4;
/** How big the blast draws, as a multiple of an ordinary impact. */
const SPLASH_FLASH = 3.4;

const BARREL_PAD = 0.2;
/** Gates span the road, so lateral tolerance matters less than depth; this is
 *  just enough that a round grazing a segment edge still counts. */
const GATE_PAD = 0.1;
const ENEMY_PAD = 0.32;

/**
 * Bullets are tested against barrels first: barrels are cover, and a round that
 * hits one must not also reach the enemy standing behind it.
 *
 * ITERATES BACKWARDS because `consume` swap-removes from `ids` — forwards would
 * skip whichever bullet got swapped into the freed slot.
 */
/**
 * A rocket's blast, after the round it came from has already been spent on the
 * thing it directly hit.
 *
 * Deliberately NOT recursive and deliberately not applied to gates: a blast that
 * filled reward segments would let a rocketeer fill two blues at once, which is
 * the exact commitment the strategic rule exists to charge for.
 */
function detonate(x: number, y: number, z: number, dmg: number): void {
  bullets.spawnImpact(x, y, z, SPLASH_FLASH);
  const splash = dmg * SPLASH_DAMAGE;
  // Once, not once per pass: a boss is a single target, and letting the blast
  // loop find it four times would quietly make rockets four times better
  // against the one enemy the difficulty curve is built around.
  // WEAPON_SPLASH, not WEAPON_ROCKET. The blast has its own row in the counter
  // table — see core/types.ts — because it lands several times on a single
  // target and the round that caused it lands once.
  boss.damageAt(x, z, SPLASH_RADIUS, splash, WEAPON_SPLASH);
  for (let i = 0; i < SPLASH_TARGETS; i++) {
    // Each pass takes the nearest remaining target; once both stop reporting a
    // hit there is nothing else in the blast and the rest of the passes are free.
    const hitBarrel = barrels.damageAt(x, z, SPLASH_RADIUS, splash) >= 0;
    const hitEnemy = enemies.damageAt(x, z, SPLASH_RADIUS, splash, WEAPON_SPLASH) >= 0;
    if (!hitBarrel && !hitEnemy) break;
  }
}

function resolveHits(): void {
  const view = bullets.bullets;
  for (let i = view.count - 1; i >= 0; i--) {
    const id = view.ids[i]!;
    const x = view.x[id]!;
    const y = view.y[id]!;
    const z = view.z[id]!;
    const dmg = view.damage[id]!;
    // WHICH GUN FIRED IT, carried all the way from the muzzle. Gates and barrels
    // ignore it — the economy is neutral to weapon choice on purpose, see
    // core/counters.ts — and the two things that can be countered read it.
    const weapon = view.weapon[id]! as WeaponKind;

    // GATES FIRST, AND THEY STOP THE ROUND. A barrier is a physical thing: the
    // stream ends at the nearest standing one and only reaches what is behind
    // once that has come apart. A blue grows as it soaks fire and breaks itself
    // at its ceiling, which is what opens the lane.
    const rocket = bullets.isRocket(id);

    if (gates.shootAt(x, z, GATE_PAD, dmg)) {
      bullets.consume(id, x, y, z);
      if (rocket) detonate(x, y, z, dmg);
      continue;
    }

    // A BOSS IS A WALL OF MEAT and stops the stream exactly as a barrier does.
    // Tested before barrels because it is the biggest thing on the road and the
    // one the player is aiming at; a round that reaches it has already passed
    // everything in front.
    if (boss.damageAt(x, z, ENEMY_PAD, dmg, weapon)) {
      bullets.consume(id, x, y, z);
      if (rocket) detonate(x, y, z, dmg);
      continue;
    }

    // AFTER the boss and BEFORE the barrels. A cage is a structure standing on
    // the road exactly as a barrel is, and it is tested first of the two so that
    // a cage placed among barrels is the thing the stream chews through rather
    // than the thing that survives because the barrels in front of it kept
    // eating rounds.
    if (ally.damageAt(x, z, BARREL_PAD, dmg)) {
      bullets.consume(id, x, y, z);
      if (rocket) detonate(x, y, z, dmg);
      continue;
    }

    if (barrels.damageAt(x, z, BARREL_PAD, dmg) >= 0) {
      bullets.consume(id, x, y, z);
      if (rocket) detonate(x, y, z, dmg);
      continue;
    }
    if (enemies.damageAt(x, z, ENEMY_PAD, dmg, weapon) >= 0) {
      bullets.consume(id, x, y, z);
      if (rocket) detonate(x, y, z, dmg);
    }
  }
}

// ── Loop ───────────────────────────────────────────────────────────────────

/** Placeholder progression: tier tracks army size until weapon pickups exist. */
function tierFor(troops: number): WeaponTier {
  if (troops < 4) return 0;
  if (troops < 40) return 1;
  return 2;
}

const road = corridor.object.children[0] as Mesh;
const roadTex = (road.material as MeshLambertMaterial).map as CanvasTexture;
let scrolled = 0;

/**
 * One simulation step. Split out of the loop callback so the debug harness at
 * the bottom of this file can drive it directly — an offscreen browser pane
 * throttles requestAnimationFrame to a dead stop, so verification screenshots
 * have to advance the world themselves.
 */
function tick(dt: number): void {
    if (state.state !== "running") return;
    // THE FREEZE. Return before anything advances and the frame repeats — the
    // renderer still runs, interpolating between two identical states.
    if (hitStopTicks > 0) {
      hitStopTicks--;
      return;
    }
    world.elapsed += dt;
    world.squadLane = touch.lane;
    world.weaponTier = tierFor(world.troops);

    // Before anything spawns: the gate module sizes a reward's target off what
    // the guns can actually deliver, and only this file knows the weapon model.
    gates.reportFirepower(
      damageOnSegment(world.troops, tierFor(world.troops), bullets.tuning, squad.radiusX),
    );

    // THE APPROACH BUILDS THE ARMY; THE RUSH ENDS THE LEVEL. The corridor only
    // runs during the approach — once the horns go the road is the player's and
    // whatever walks onto it.
    if (phase === "approach") {
      approached += world.scrollSpeed * dt;
      if (contentSpawning) {
        // Distance, not time. Spacing is authored in metres of road, so it holds
        // even if the world speeds up or slows down.
        const due = director.advance(world.scrollSpeed * dt);
        if (due) place(due.what, SPAWN_Z, due.side);
      }
      if (approached >= levelDistance()) beginRush();
    } else if (rushLeft > 0 && !boss.active) {
      rushTimer -= dt;
      if (rushTimer <= 0) {
        rushLeft--;
        place("boss", SPAWN_Z, rushLeft % 2 === 0 ? 1 : -1);
      }
    }

    // 1. Squad first: it writes world.squadCenter, which everything below reads.
    squad.update(dt, world);
    // Immediately after, while the bodies that just arrived or fell are current.
    drainSquadEvents();

    // 2. Muzzles sit at the blob's front edge, spread across its width — firing
    //    from a single point reads as one soldier no matter how many there are.
    bullets.setMuzzle(
      squad.center.x,
      squad.center.y + 1,
      squad.center.z - squad.radiusZ,
      squad.radiusX,
    );
    // setMuzzle still supplies the blob centre and width for aim; setShooters
    // is what gives each soldier its own stream origin.
    // ROOM IS RESERVED FOR THE ROBOT BEFORE THE CROWD IS SAMPLED, not after.
    // The crowd fills every stream slot it is offered, so appending the ally
    // afterwards gave it zero streams at exactly the army sizes where the stream
    // cap binds — the robot would have gone quiet in the second half of every
    // level and nothing would have said why.
    const squadCap = ally.active ? MAX_STREAMS - ALLY_STREAMS : MAX_STREAMS;
    const reported = squad.sampleShooters(shooters, squadCap);
    // Same limit, deliberately: the two samplers share their index arithmetic,
    // and a divergence between them puts a rocket in an ordinary soldier's hands.
    squad.sampleShooterKinds(shooterKinds, squadCap);
    // THE ROBOT IS A BANK OF STREAMS AT ONE POINT, appended to the crowd's own.
    // It owns no bullet code — see the header of entities/ally.ts — so this is
    // the whole of its firepower: ten rocket streams from its muzzle, which is
    // worth roughly twenty riflemen. Appended rather than mixed in so the
    // crowd's own sampling is untouched.
    let streams = reported;
    if (ally.active) {
      const room = Math.min(ALLY_STREAMS, MAX_STREAMS - streams);
      for (let i = 0; i < room; i++) {
        shooters[streams]!.set(ally.muzzleX, ally.muzzleY, ally.muzzleZ);
        shooterKinds[streams] = WEAPON_ROCKET;
        streams++;
      }
    }
    bullets.setShooters(shooters, streams);
    // Immediately after, against the same buffers: which of those soldiers is
    // carrying what, so a rocketeer's stream actually fires rockets.
    bullets.setShooterKinds(shooterKinds, streams);
    bullets.update(dt, world);

    // 3. Targets move, then get shot — so a hit lands where the barrel is now,
    //    not where it was last tick.
    barrels.update(dt, world);
    enemies.update(dt, world);
    // With the other targets, and before the rounds are resolved against it, so
    // a hit lands where the boss is now — it moves fast during a charge and a
    // tick of lag there reads as rounds passing through it.
    boss.update(dt, world);
    // After the boss and before the hits are resolved, for the same reason the
    // boss is: the cage has to be where it is now when a round arrives at it.
    ally.update(dt, world);
    // The dying boss's blast chain. Driven here rather than from the boss module
    // because the flashes belong to the bullet system, and an entity module does
    // not reach into another one.
    if (blastsLeft > 0) {
      blastTimer -= dt;
      if (blastTimer <= 0) {
        blastTimer = BOSS_BLAST_GAP;
        blastsLeft--;
        bullets.spawnImpact(
          blastX + (Math.random() - 0.5) * BOSS_DEATH_SPREAD,
          0.5 + Math.random() * 3.6,
          blastZ + (Math.random() - 0.5) * BOSS_DEATH_SPREAD * 0.6,
          BOSS_DEATH_FLASH * (0.6 + Math.random() * 0.7),
        );
      }
    }
    if (boss.fighting) bossBar.set(boss.hp);
    else if (!boss.active && bossShowing) bossBar.hide();
    bossShowing = boss.active;
    // BEFORE the seating pass, and it has to be here: pickups was in
    // `renderables` (so it drew) but its update was never called, which is the
    // whole of the "floating objects" bug. Without a tick its clock never
    // advanced, so the staleness retirement never fired; its previous-position
    // history never refreshed, so render interpolated against a frozen frame;
    // and a collected prize never flew, never landed and never retired. A gold
    // ring simply stopped in mid-air while its barrel drove on underneath.
    pickups.update(dt, world);
    barrels.forEachLive(seatRider);
    resolveHits();

    // 4. Gates resolve against the settled squad position.
    gates.update(dt, world);
    // Immediately after, while this row's segments are the only ones in the
    // accumulator — see the note on the streak rule above.
    settleRow();

    // 5. Feedback last: it reacts to everything above, within the same tick.
    floaters.update(dt, world);
    growthFx.update(dt, world);
    bossBar.update(dt, world);
    // Last of the readouts, so it reports the count this tick actually ended on.
    troopCount.update(dt, world);
    loadout.update(dt, world);
    streak.update(dt, world);
    commander.update(dt, world);
    netPop.update(dt, world);

    // The bridge scrolls too, so its towers sweep past and their shadows move
    // across the crowd rather than lying on the deck like paint.
    corridor.update(dt, world.scrollSpeed);
    scrolled += world.scrollSpeed * dt;

    // Last, so it steps on the count this tick actually ended with — a gate
    // that pays 200 troops should move the camera on the same beat the +1s
    // bloom, not one tick later.
    zoom.update(dt, world.troops, squad.center.x);
    world.zoom = zoom.distance;

    if (world.troops <= 0) {
      wipes++;
      // The calibration harness drives thousands of runs and must not stop on a
      // card nobody is there to press. A real player gets the screen.
      if (harnessMode) resetRun();
      else failLevel();
    }
}

function draw(alpha: number): void {
  for (const system of renderables) system.render(alpha, world);
  // The 6 is metres per texture tile — `roadTexture()` sets repeat.y to
  // CORRIDOR_LENGTH / 6, so this is that division undone. It used to be written
  // as a hardcoded 70 / repeat.y, which was the same number only for as long as
  // the corridor happened to be 70 m long.
  roadTex.offset.y = (scrolled / (CORRIDOR_LENGTH / roadTex.repeat.y)) % 1;
  stage.renderer.render(stage.scene, stage.camera);
}

const loop = new GameLoop({
  update: tick,
  render: draw,
  onStats(stats) {
    bus.emit("frame:stats", stats);
  },
});

mountPerfOverlay(ui, stage.renderer);

state.transition("briefing");
state.transition("running");
commander.say("opening");
world.troops = START_TROOPS;
// The corridor has to be stocked before the first frame, or the run opens on an
// empty road and the player waits three seconds for anything to happen.
primeCorridor();
loop.start();

// A backgrounded run should not drain a phone battery, and stopping also means
// the loop never has a multi-second delta to swallow on return.
document.addEventListener("visibilitychange", () => {
  if (document.hidden) loop.stop();
  else loop.start();
});

/**
 * Damage a single barrel actually takes on one full approach, at a given troop
 * count. Dev-only — the `import.meta.env.DEV` block below is its only caller,
 * so production strips it along with the harness.
 *
 * Barrel HP is the one number in the game that cannot be reasoned about from
 * the fire rate alone: a barrel is only in range for the last third of its
 * journey, the squad's muzzles move forward as the blob deepens, and the
 * convergence cone means an off-lane barrel catches a fraction of the stream.
 * Three shipped calibration bugs came from doing this arithmetic on paper, so
 * this measures it instead — spawn one barrel with more HP than any weapon can
 * chew through, run the real update order, and count what it lost.
 *
 * Destructive: it clears the corridor and leaves the run reset behind it.
 */
function probeDamagePerPass(troops: number, lane = 0, barrelLane = lane): {
  troops: number;
  tier: WeaponTier;
  /** Total damage the barrel absorbed between spawn and passing the squad. */
  damage: number;
  /** Seconds it spent actually taking fire — the real kill window. */
  window: number;
  /** Muzzle DPS the squad was producing, for comparison against on-target. */
  onTargetDps: number;
} {
  const PROBE_HP = 1e9;
  const dt = 1 / 60;

  contentSpawning = false;
  gates.setAutoSpawn(false);
  // Rows already in the corridor MUST go too, not just future ones. A live gate
  // resolving mid-probe pays or charges troops, which silently re-tiers the
  // weapon; at one troop a red segment zeroes the count, trips resetRun(), and
  // clears the probe barrel before it ever reaches the kill zone — which reads
  // as "one soldier deals no damage" rather than as a broken measurement.
  gates.reset();
  barrels.clear();
  enemies.clear();
  // A boss mid-probe would eat the rounds being counted, and its patience clock
  // would charge the probe troops halfway through the measurement.
  boss.clear();
  world.troops = clamp(Math.round(troops), 1, MAX_TROOPS);
  touch.lane = clamp(lane, -1, 1);

  // Settle first. The blob's width and depth grow with the count and the muzzle
  // line rides its front edge, so probing before the springs converge measures
  // the wrong geometry.
  for (let i = 0; i < 90; i++) tick(dt);

  // `barrelLane` defaults to the squad's own lane — the head-on case. Passing a
  // different one measures COVERAGE: with fire travelling as a parallel curtain,
  // a barrel off to one side takes only the share of the stream that overlaps
  // it, which is the mechanic the whole width of the crowd now buys.
  const id = barrels.spawn(barrelLane, SPAWN_Z, PROBE_HP);
  let ticks = 0;
  let last = PROBE_HP;
  // First and last tick on which a round actually landed. The window is the
  // span BETWEEN them, not the count of ticks that scored — at one troop most
  // ticks are empty, and counting only the scoring ones reports a kill window
  // twelve times shorter than the barrel really spent under fire.
  let firstHit = -1;
  let lastHit = -1;

  // 58 m at 6 m/s is ~10 s; the cap is a runaway guard, not the exit condition.
  while (ticks < 60 * 20) {
    tick(dt);
    ticks++;
    const hp = barrels.hpOf(id);
    if (hp < 0) break; // destroyed or recycled past the camera
    if (hp < last) {
      if (firstHit < 0) firstHit = ticks;
      lastHit = ticks;
    }
    last = hp;
    let z = -Infinity;
    barrels.forEachLive((bid, _tag, _x, _topY, bz) => {
      if (bid === id) z = bz;
    });
    if (z >= squad.center.z) break;
  }

  const damage = PROBE_HP - last;
  const window = firstHit < 0 ? 0 : (lastHit - firstHit + 1) * dt;
  const tier = world.weaponTier;

  contentSpawning = true;
  gates.setAutoSpawn(true);
  resetRun();

  return {
    troops: Math.round(troops),
    tier,
    damage: Math.round(damage),
    window: Number(window.toFixed(2)),
    onTargetDps: window > 0 ? Math.round(damage / window) : 0,
  };
}

/**
 * Run `seconds` of play with a simple autopilot steering at the best segment of
 * the nearest gate, and report the troop curve. Dev-only, like the probe.
 *
 * "Steer at the biggest number" is not a good player, and that is the point: it
 * is a REPEATABLE one. A growth target and a failure rate are both properties of
 * the content rather than of anybody's thumbs, so measuring them needs a hand
 * that plays the same way every time.
 */
/**
 * Push a wanted lane out of a boss's painted danger zone.
 *
 * `gates.bestLane()` scores gate rows and knows nothing about bosses, which is
 * correct — the gate module must not learn about entities. But a bot that walks
 * into every slam is the same class of measurement error as the one that scored
 * an unfillable blue at face value: it reports a game that is harder than the
 * one a player with eyes is playing. This is the smallest correction that fixes
 * that, and no more — it dodges, it does not aim.
 */
function dodgeBoss(want: number): number {
  if (!boss.dangerActive) return want;
  const clear = boss.dangerHalf + Math.max(0.6, world.squadHalfWidth * 0.6);
  const lo = (boss.dangerX - clear) / CORRIDOR_HALF_WIDTH;
  const hi = (boss.dangerX + clear) / CORRIDOR_HALF_WIDTH;
  if (want <= lo || want >= hi) return want;
  // Out the nearer side, unless that side is off the road.
  const outLeft = lo > -1 ? lo : Infinity;
  const outRight = hi < 1 ? hi : Infinity;
  if (outLeft === Infinity && outRight === Infinity) return want;
  return Math.abs(want - outLeft) <= Math.abs(want - outRight) ? outLeft : outRight;
}

function autopilot(seconds = 120, sampleEvery = 5, reaction = 0.3) {
  harnessMode = true;
  const dt = 1 / 60;
  const ticks = Math.round(seconds / dt);
  const samples: { t: number; troops: number }[] = [];
  const before = wipes;
  let peak = world.troops;
  let worstDrop = 0;
  let prev = world.troops;
  // Focus is sampled EVERY TICK, not at the end of a slice. Sampling it between
  // autopilot calls reported a mean of 0.83 because the bot always ends a slice
  // settled — the measurement was of the pauses, not of the run.
  const levelAtStart = world.level;
  // REACTION TIME, because a bot that re-decides sixty times a second is not a
  // measurement of the game — it is a measurement of the game played perfectly.
  // At reaction 0 the autopilot wiped 0 runs in 16 while a human playtester was
  // losing armies, and tuning the difficulty until an optimal player dies would
  // have made the game unplayable for anyone else. Re-deciding every ~0.3 s puts
  // it in the range a thumb actually operates in, and it is the setting the
  // brief's per-level failure rates should be read against.
  let sinceDecision = Infinity;
  for (let i = 0; i < ticks; i++) {
    sinceDecision += dt;
    if (sinceDecision >= reaction) {
      sinceDecision = 0;
      const want = gates.bestLane();
      if (!Number.isNaN(want)) touch.lane = clamp(dodgeBoss(want), -1, 1);
    }
    tick(dt);
    peak = Math.max(peak, world.troops);
    // Biggest single-tick loss as a share of what was standing. A wipe is the
    // tail of this distribution, so watching it is how a change to the penalty
    // model gets judged on more than one unlucky run.
    if (prev > 0) worstDrop = Math.max(worstDrop, (prev - world.troops) / prev);
    prev = world.troops;
    if (i % Math.max(1, Math.round(sampleEvery / dt)) === 0) {
      samples.push({ t: Number((i * dt).toFixed(1)), troops: world.troops });
    }
  }
  harnessMode = false;
  return {
    samples,
    wipes: wipes - before,
    peak,
    worstDrop: Number(worstDrop.toFixed(3)),
    final: world.troops,
    // THE MEASURE THAT REPLACED THE MEDIAN. With levels, the army resets every
    // level, so "troops at the 110-second mark" is whatever point of whatever
    // level the clock happened to stop in — a number with no meaning. How far
    // the run GOT is the honest one.
    levelsCleared: world.level - levelAtStart,
  };
}

/**
 * HEADLESS DRIVE — dev builds only, stripped from production.
 *
 * An offscreen browser pane reports `document.hidden` and throttles
 * requestAnimationFrame to *zero* frames, so screenshotting a live run captures
 * whatever frame it happened to freeze on. This lets a verification pass pose
 * the world at an exact state — "45 troops, gate 6 metres out" — and render
 * that deterministically, which is a better way to compare against a specific
 * reference frame than trying to catch the moment live.
 */
if (import.meta.env.DEV) {
  Object.assign(window, {
    __overkill: {
      world,
      /** The live scene graph. For asking what is ACTUALLY in the world rather
       *  than what a module believes it drew — the only way to chase down a
       *  stray instance without a module volunteering a debug accessor. */
      scene: stage.scene,
      probeDamagePerPass,
      /** Sweep the probe across troop counts. This is the barrel HP curve. */
      damageCurve(counts: readonly number[] = [1, 2, 3, 5, 8, 12, 20, 30, 50, 80, 120]) {
        return counts.map((n) => probeDamagePerPass(n));
      },
      setSpawning(on: boolean): void {
        contentSpawning = on;
      },
      /** Drop one placement at an exact Z, bypassing the director. For posing a
       *  single piece of content in an otherwise empty corridor — the only way
       *  to photograph one thing without the rest of the road in frame. */
      place(what: Placement, z = SPAWN_Z): void {
        place(what, z);
      },
      /** Put a named boss on the road at an exact distance, ignoring the
       *  cadence. Screenshotting a boss otherwise means playing 28 seconds of
       *  corridor first, and the pane cannot run 28 seconds. */
      boss(kind: BossKind = "brute", z = SPAWN_Z, side = 1): void {
        boss.clear();
        boss.spawn(kind, bossHp(kind, bossIndex), side, z);
        bossBar.reset(boss.maxHp);
      },
      /** Where the ally is and what it is doing. The robot takes station off the
       *  crowd's live width, so "is it in frame" is a question with a number. */
      allyStats() {
        return {
          caged: ally.caged,
          active: ally.active,
          cageFraction: Number(ally.cageFraction.toFixed(3)),
          x: Number(ally.muzzleX.toFixed(2)),
          z: Number(ally.muzzleZ.toFixed(2)),
        };
      },
      /** What the live boss is doing, for grading a pose against a reference. */
      bossStats() {
        return { kind: boss.kind, hp: boss.hp, max: boss.maxHp, x: boss.x, z: boss.z, side: boss.side };
      },
      /** Pose either level card without having to play a level to it. */
      showCleared(): void {
        biggestCrowd = Math.max(biggestCrowd, world.troops);
        bossesKilled = rushSize();
        clearLevel();
      },
      showFailed(): void {
        failLevel();
      },
      autopilot,
      /**
       * Play `runs` fresh runs of `seconds` each and report the distribution.
       *
       * The brief specs a failure RATE — 1 in 8 at levels 1–2 — and a rate is
       * not a thing one run can measure. Half the tuning arguments on this
       * project have been about whether a single unlucky playthrough meant the
       * economy was wrong; this is what replaces that with a number.
       */
      sample(runs = 12, seconds = 110, reaction = 0.3) {
        const out: { final: number; peak: number; wiped: boolean; levels: number }[] = [];
        for (let r = 0; r < runs; r++) {
          const before = wipes;
          // BACK TO LEVEL 1 WITH NOTHING, every run. Without this the level and
          // the perks carried between samples, so run 30 was played at level 30
          // by an army with thirty upgrades — the sample measured a difficulty
          // ramp rather than thirty-two comparable runs.
          world.level = 1;
          for (const k of Object.keys(perks) as PerkId[]) perks[k] = 0;
          resetRun();
          world.elapsed = 0;
          const run = autopilot(seconds, seconds, reaction);
          out.push({
            final: run.final,
            peak: run.peak,
            wiped: wipes - before > 0,
            levels: run.levelsCleared,
          });
        }
        const finals = out.map((r) => r.final).sort((a, b) => a - b);
        const levels = out.map((r) => r.levels).sort((a, b) => a - b);
        return {
          runs: out.length,
          wiped: out.filter((r) => r.wiped).length,
          medianLevels: levels[Math.floor(levels.length / 2)],
          levels,
          median: finals[Math.floor(finals.length / 2)],
          min: finals[0],
          max: finals[finals.length - 1],
          peaks: out.map((r) => r.peak),
          finals,
        };
      },
      /** Face values of every live unresolved gate segment, nearest row first. */
      gateValues(): number[] {
        return gates.debugValues();
      },
      /** Place an exact gate row, for testing how a wide crowd resolves it. */
      spawnGate(values: number[], z = SPAWN_Z): void {
        gates.spawnRow(values, z);
      },
      /** Every payout since the last clear, as [troops, wasReward] pairs. */
      payouts(): { troops: number; value: number; share: number; reward: boolean }[] {
        return payoutLog.slice();
      },
      clearPayouts(): void {
        payoutLog.length = 0;
      },
      /** Live round population and its extent — the number to hold against a
       *  reference frame when asking whether our fire is too dense. */
      bulletStats() {
        const v = bullets.bullets;
        let minX = Infinity;
        let maxX = -Infinity;
        let minZ = Infinity;
        let maxZ = -Infinity;
        for (let i = 0; i < v.count; i++) {
          const id = v.ids[i]!;
          minX = Math.min(minX, v.x[id]!);
          maxX = Math.max(maxX, v.x[id]!);
          minZ = Math.min(minZ, v.z[id]!);
          maxZ = Math.max(maxZ, v.z[id]!);
        }
        return {
          count: v.count,
          minX,
          maxX,
          minZ,
          maxZ,
          muzzleZ: squad.center.z - squad.radiusZ,
        };
      },
      /** Advance `ticks` fixed steps, then draw once. */
      step(ticks = 60): void {
        for (let i = 0; i < ticks; i++) tick(1 / 60);
        draw(0);
      },
      setTroops(n: number): void {
        world.troops = clamp(Math.round(n), 0, MAX_TROOPS);
        if (world.elites > world.troops) world.elites = world.troops;
      },
      setElites(n: number): void {
        world.elites = clamp(Math.round(n), 0, world.troops);
        armCarriers();
      },
      /** Arm N troops with each weapon. For posing the crowd in a screenshot. */
      setCarriers(gunners: number, rocketeers: number): void {
        world.gunners = clamp(Math.round(gunners), 0, world.troops);
        world.rocketeers = clamp(Math.round(rocketeers), 0, world.troops);
        armCarriers();
      },
      setLane(n: number): void {
        touch.lane = clamp(n, -1, 1);
      },
      pay: payTroops,
      stats: () => ({
        troops: world.troops,
        elites: world.elites,
        gunners: world.gunners,
        rocketeers: world.rocketeers,
        flamers: world.flamers,
        freezers: world.freezers,
        enemies: enemies.liveCount,
        enemyHp: Number(enemies.firstHpFraction.toFixed(3)),
        firepower: Number(world.firepower.toFixed(2)),
        fireRate: Number(world.fireRate.toFixed(2)),
        tier: world.weaponTier,
        elapsed: Number(world.elapsed.toFixed(2)),
        squadX: Number(squad.center.x.toFixed(2)),
        streak: streak.count,
        rowsWon,
        rowsLost,
        streakMult: streak.multiplier,
        radiusX: Number(squad.radiusX.toFixed(2)),
        calls: stage.renderer.info.render.calls,
        zoom: Number(world.zoom.toFixed(3)),
        radiusZ: Number(squad.radiusZ.toFixed(2)),
        tris: stage.renderer.info.render.triangles,
      }),
    },
  });
}

// Debug sliders are opt-in: lil-gui is ~30kB and has no business in a playtest
// build the player is holding.
if (new URLSearchParams(location.search).has("debug")) {
  void import("lil-gui").then(({ default: GUI }) => {
    const gui = new GUI({ title: "Overkill" });
    gui.add(world, "troops", 0, MAX_TROOPS, 1).listen();
    gui.add(world, "scrollSpeed", 0, 24, 0.5);
    gui
      .add({ grow: 10 }, "grow", 1, 50, 1)
      .name("test growth (+N)")
      .onFinishChange((n: number) => payTroops(n));
  });
}
